import { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { api, getToken } from '../api';
import ProfilePrompt from '../components/ProfilePrompt';
import { possessivePronoun } from '../utils';
import { formatConstitution } from '../lib/constitutionLabels';

const TIER_GRADIENTS = {
  'Soul Mate':  'linear-gradient(160deg, #1a1408 0%, #2a1f06 40%, #0d0d0d 100%)',
  'Top Match':  'linear-gradient(160deg, #0e0c18 0%, #1a1630 40%, #0d0d0d 100%)',
  'Match':      'linear-gradient(160deg, #080e0a 0%, #0f1e12 40%, #0d0d0d 100%)',
  default:      'linear-gradient(160deg, #0d0d0d 0%, #141414 100%)',
};

const TIER_CLASS = {
  'Soul Mate': 'tier-soul-mate',
  'Top Match': 'tier-top-match',
  'Match': 'tier-match',
};

const generateSummary = (match) => {
  const { tier } = match;
  if (tier === 'Soul Mate') {
    return `Your patterns align at a level we rarely see. This one is worth your full attention.`;
  }
  if (tier === 'Top Match') {
    return `Strong alignment across life vision and daily rhythms. Your energies are built to complement each other.`;
  }
  return `Meaningful overlap in how you approach love and life. A connection worth exploring with presence.`;
};

export default function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [mutualIds, setMutualIds] = useState(new Set());
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passingMatch, setPassingMatch] = useState(null);
  const [passReason, setPassReason] = useState('');
  const [passLoading, setPassLoading] = useState(false);
  const [isPlus, setIsPlus] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeCode, setUpgradeCode] = useState('');
  const [upgradeError, setUpgradeError] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const swipeStartY = useRef(null);
  const swipeStartX = useRef(null);

  useEffect(() => {
    if (!getToken()) return;
    api.get('/matches')
      .then(data => { setMatches(data); setLoading(false); })
      .catch(err => {
        if (err.message === 'session_expired') { navigate('/login'); return; }
        setError(err.message);
        setLoading(false);
      });
    api.get('/mutual-matches')
      .then(data => setMutualIds(new Set(data.map(m => m.matched_user_id))))
      .catch(() => {});
    api.get('/profile')
      .then(p => setIsPlus(p.subscription_tier === 'plus'))
      .catch(() => {});
  }, [navigate]);

  if (!getToken()) return <Navigate to="/login" replace />;

  if (localStorage.getItem('swipe_test_done') && !localStorage.getItem('profile_read_shown')) {
    return <Navigate to="/onboarding/profile-read" replace />;
  }

  if (loading) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
        <div className="label" style={{ marginBottom: 20 }}>Could not load matches</div>
        <div className="error-msg" style={{ marginBottom: 32 }}>{error}</div>
        <button className="btn btn-gold" style={{ maxWidth: 240, marginBottom: 12 }} onClick={() => navigate('/login')}>
          Sign in again
        </button>
        <button className="btn-ghost" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  if (!matches.length) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
        <div style={{ color: 'var(--muted)', fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 300, marginBottom: 16 }}>
          No matches yet
        </div>
        <div style={{ color: 'var(--very-muted)', fontSize: 13, fontWeight: 300, lineHeight: 1.7 }}>
          Complete your profile to start receiving matches each week.
        </div>
        <button className="btn btn-gold" style={{ maxWidth: 240, marginTop: 40 }} onClick={() => navigate('/onboarding/profile')}>
          Complete profile
        </button>
      </div>
    );
  }

  const match = matches[current];
  const isLocked = !isPlus && current >= 3;
  const gradient = TIER_GRADIENTS[match.tier] || TIER_GRADIENTS.default;
  const tierClass = TIER_CLASS[match.tier] || 'tier-match';

  const next = () => setCurrent(c => Math.min(matches.length - 1, c + 1));
  const prev = () => setCurrent(c => Math.max(0, c - 1));

  const handleTouchStart = (e) => {
    swipeStartY.current = e.touches[0].clientY;
    swipeStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (swipeStartY.current === null || isLocked) return;
    const deltaY = swipeStartY.current - e.changedTouches[0].clientY;
    const deltaX = Math.abs(swipeStartX.current - e.changedTouches[0].clientX);
    swipeStartY.current = null;
    swipeStartX.current = null;
    // Only trigger if more vertical than horizontal and > 60px up
    if (deltaY > 60 && deltaX < 40) {
      navigate(`/match/${match.user_id}`, { state: { match } });
    }
  };

  const submitUpgradeCode = async () => {
    if (!upgradeCode.trim()) return;
    setUpgrading(true);
    setUpgradeError('');
    try {
      await api.post('/upgrade', { code: upgradeCode.trim() });
      setIsPlus(true);
      setShowUpgrade(false);
      setUpgradeCode('');
    } catch {
      setUpgradeError('Invalid code — try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const submitPass = async (reason) => {
    const target = passingMatch;
    setPassLoading(true);
    try {
      await api.post('/matches/feedback', {
        target_user_id: target.user_id,
        feedback: 'not_for_me',
        ...(reason?.trim() ? { pass_reason: reason.trim() } : {}),
      });
    } catch {
      // don't block navigation on feedback failure
    } finally {
      setPassLoading(false);
      setPassingMatch(null);
      setPassReason('');
      next();
    }
  };

  return (
    <div className="matches-screen">
      <div className="match-card" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* Top bar */}
        <div className="match-top-bar">
          <span className="match-week-label">This week</span>
          {matches.length > 1 && (
            <div className="match-dots">
              {Array.from({ length: matches.length }, (_, i) => {
                let cls = 'match-dot';
                if (isPlus || i < 3) cls += ' active';
                if (i === current) cls += ' current';
                return <div key={i} className={cls} onClick={() => !isLocked && setCurrent(i)} />;
              })}
            </div>
          )}
        </div>

        {/* Photo / gradient area */}
        <div className="match-photo-area">
          <div className="match-gradient-bg" style={{ background: gradient }} />
          <div className="match-initial-watermark">{match.name?.[0] || '?'}</div>
          {match.photo_url && (
            <img
              src={match.photo_url}
              alt={match.name}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
          <div className="match-photo-overlay" />

          {/* Tap zones for navigation */}
          {!isLocked && matches.length > 1 && (
            <>
              <div
                onClick={prev}
                style={{
                  position: 'absolute', left: 0, top: 0, width: '40%', height: '100%',
                  zIndex: 5, cursor: current > 0 ? 'pointer' : 'default',
                }}
              />
              <div
                onClick={next}
                style={{
                  position: 'absolute', right: 0, top: 0, width: '60%', height: '100%',
                  zIndex: 5, cursor: current < matches.length - 1 ? 'pointer' : 'default',
                }}
              />
            </>
          )}

          {/* Locked overlay */}
          {isLocked && (
            <div className="match-locked-overlay">
              <div className="display-sm" style={{ color: '#fff' }}>Two more await</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 300, lineHeight: 1.6 }}>
                Unlock your final two matches to see if your best is still ahead.
              </div>
              <button className="btn btn-gold" style={{ maxWidth: 240, marginTop: 8 }} onClick={() => setShowUpgrade(true)}>
                $25 / month — Unlock
              </button>
              <button className="btn-ghost" onClick={() => setCurrent(0)}>
                Maybe later
              </button>
            </div>
          )}

          {/* Photo info */}
          {!isLocked && (
            <div className="match-photo-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className={`tier-badge ${tierClass}`}>
                  <div className="tier-dot" />
                  {match.tier}
                </div>
                {mutualIds.has(match.user_id) && (
                  <div className="mutual-indicator">mutual match</div>
                )}
              </div>
              <div className="match-name">{match.name}</div>
              {match.location && (
                <div className="match-location-text">{match.location}</div>
              )}
              {match.dosha_type && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 300, marginTop: 3, letterSpacing: 0.5 }}>
                  {formatConstitution(match.dosha_type)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info area */}
        <div className="match-info-area">
          {!isLocked && (
            <>
              <div className="match-summary-text">{generateSummary(match)}</div>
              <div className="match-actions">
                {matches.length > 1 && (
                  <button
                    className="match-action-btn pass"
                    onClick={() => setPassingMatch(match)}
                    disabled={current >= matches.length - 1}
                  >
                    Pass
                  </button>
                )}
                <button
                  className="match-action-btn profile"
                  onClick={() => navigate(`/match/${match.user_id}`, { state: { match } })}
                >
                  {possessivePronoun(match.gender)} profile
                </button>
                {matches.length > 1 && (
                  <button
                    className="match-action-btn next"
                    onClick={next}
                    disabled={current >= matches.length - 1}
                  >
                    Next →
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Pass reason overlay */}
        {passingMatch && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(0,0,0,0.96)',
            display: 'flex', flexDirection: 'column',
            padding: '48px 28px 36px',
          }}>
            <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 20 }}>
              What didn't land?
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 28 }}>
              Paula uses this to sharpen your next matches. Say anything — or skip.
            </div>
            <textarea
              className="field"
              style={{ height: 130, resize: 'none', marginBottom: 20, fontFamily: 'inherit', lineHeight: 1.7 }}
              placeholder="They seemed too… / I'm really looking for…"
              value={passReason}
              onChange={e => setPassReason(e.target.value)}
              autoFocus
            />
            <button
              className="btn btn-gold"
              onClick={() => submitPass(passReason)}
              disabled={passLoading || !passReason.trim()}
            >
              {passLoading ? <span className="spinner" /> : 'Send to Paula'}
            </button>
            <button
              className="btn-ghost"
              style={{ marginTop: 16, fontSize: 13 }}
              onClick={() => submitPass(null)}
              disabled={passLoading}
            >
              Skip and not explain
            </button>
          </div>
        )}
      </div>
      <ProfilePrompt />

      {showUpgrade && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 32,
        }}>
          <div style={{
            width: '100%', maxWidth: 340,
            background: '#0d0d0d', border: '0.5px solid rgba(201,168,76,0.25)',
            padding: 28,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--gold)', marginBottom: 16 }}>UPGRADE TO MATCHMAKER+</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 300, lineHeight: 1.6, marginBottom: 24 }}>
              Enter your access code to unlock all matches, voice chat, and more.
            </div>
            <input
              autoFocus
              value={upgradeCode}
              onChange={e => { setUpgradeCode(e.target.value); setUpgradeError(''); }}
              onKeyDown={e => e.key === 'Enter' && submitUpgradeCode()}
              placeholder="Access code"
              style={{
                width: '100%', background: 'rgba(255,255,255,0.04)',
                border: '0.5px solid rgba(255,255,255,0.12)',
                color: 'var(--text)', fontFamily: 'var(--font-ui)',
                fontSize: 14, fontWeight: 300, padding: '11px 14px',
                outline: 'none', boxSizing: 'border-box', marginBottom: 8,
              }}
            />
            {upgradeError && (
              <div style={{ fontSize: 12, color: 'rgba(255,80,60,0.7)', marginBottom: 12 }}>{upgradeError}</div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                onClick={() => { setShowUpgrade(false); setUpgradeCode(''); setUpgradeError(''); }}
                style={{
                  flex: 1, padding: '10px 0', background: 'transparent',
                  border: '0.5px solid rgba(255,255,255,0.1)',
                  color: 'var(--very-muted)', fontSize: 13, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitUpgradeCode}
                disabled={!upgradeCode.trim() || upgrading}
                style={{
                  flex: 1, padding: '10px 0',
                  background: upgradeCode.trim() ? 'rgba(201,168,76,0.12)' : 'transparent',
                  border: `0.5px solid ${upgradeCode.trim() ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: upgradeCode.trim() ? 'var(--gold)' : 'var(--very-muted)',
                  fontSize: 13, cursor: upgradeCode.trim() ? 'pointer' : 'default',
                }}
              >
                {upgrading ? 'Checking...' : 'Unlock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
